/* eslint-disable no-useless-return */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProfileField, ProfileDataType } from '../../types/profile';
import { PersonalInvite } from '../../types/invites';
import { connectionService, invitationService, profileService, communityService } from '../../services';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import useDeleteParams from '../../hooks/useDeleteParams';
import { SuccessPageState } from '../../types/navigation';
import { convertPersonalInviteToProfileFields } from '../../utils/formatUtils';
import AddFieldsHeader from '../../components/headers/AddFieldsHeader';
import ContactConfirmationModal from '../../components/modals/LocalContactConfirmationModal';
import useAuth from '../../hooks/useAuth';
import useProfileFields from '../../hooks/useProfileFields';
import { filterPresentFields } from '../../utils/filterUtils';
import { isEmptyField } from '../../utils/validationUtils';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';

export default function AuthenticatedPersonalInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  const auth = useAuth();
  useDeleteParams('link');

  const token = params.get('invitationId');

  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [invite, setInvite] = useState<PersonalInvite>();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [noDataSharedModalOpen, setNoDataSharedModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }

    invitationService
      .getPersonalInvite(token)
      .then((inv) => {
        setInvite(inv);
        const preSelectedValues = filterPresentFields(
          profileFields,
          convertPersonalInviteToProfileFields(inv).filter((f): boolean => !isEmptyField(f)),
        );

        setSelectedData(preSelectedValues);
        if (auth?.alias !== `${inv?.data.firstName!}+${inv?.data.lastName!}`) {
          setConfirmationModalOpen(true);
        }
      })
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setNoDataSharedModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    await connectionService.createPersonalConnection(
      token!,
      allSelectedData.map((x) => +x.id!),
    );

    try {
      await communityService.fetchCommunities();
    } catch (error) {
      // Ignore - if refresh fails, the list will update on next load
    }

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.invite',
      translationOptions: {
        requesterName: invite?.inviter.firstName!,
      },
      requesterId: `${invite?.inviter.userId!}`,
      requesterAlias: `${invite?.inviter.firstName}+${invite?.inviter.lastName}`,
    };
    navigate('/success', { state });
    toast.success(t('toast.success.connectionCreated'));
  };

  const requesterName = invite?.inviter.firstName || '';

  const fields = invite ? convertPersonalInviteToProfileFields(invite) : [];

  const addFields = (fieldsToAdd: ProfileField[]): void => {
    setSelectedData([
      ...selectedData.filter((f) => !fieldsToAdd.some((x) => x.dataType === f.dataType)),
      ...fieldsToAdd,
    ]);
  };

  return (
    <>
      <ResponsePageWrapper>
        <ResponsePageWrapper.Header>
          <AddFieldsHeader requesterName={requesterName} fields={fields} addFields={addFields} />
        </ResponsePageWrapper.Header>
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">{t('page.personalInvite.message', { requesterName })}</p>
          </div>

          <UserDataForm
            selectedData={selectedData || []}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            hasSelectAll
          />
        </div>

        <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
            {t('page.shared.shareData')}
          </Button>
        </div>
      </ResponsePageWrapper>
      <ContactConfirmationModal
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        info={{
          alias: `${invite?.data.firstName!}+${invite?.data.lastName!}`,
          receivedData: fields,
        }}
        inviterName={`${invite?.inviter.firstName!} ${invite?.inviter.lastName}`}
      />
      <NoDataSharedModal
        open={noDataSharedModalOpen}
        setOpen={setNoDataSharedModalOpen}
        requesterName={requesterName}
        handleComplete={handleSubmit}
      />
    </>
  );
}
